import { z } from 'zod';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { discoveryKeys } from './discovery';
import { CoverageChangeSchema } from './discovery-sessions';
import { newIdempotencyKey } from './engagement-requirements';

/**
 * Transcript review in Sessions (T-0187, E12-F05) over the T-0176
 * transcripts API and the T-0177 evidence API, under
 * `/api/v1/engagements/:id/discovery`.
 *
 *   - Proposed evidence never counts until confirmed (rule 12). Confidence
 *     is calibrated by code (Decision 53.1); `preselected` (0.85 or more)
 *     and `blocked` (unmapped speaker, the interviewer, an unresolved
 *     possible duplicate; rule 8) come from the API.
 *   - `:preview-confirm` computes the coverage effect of a selection and
 *     writes nothing; the confirm bar shows it before the consultant acts.
 *   - Writes carry an `Idempotency-Key`; confirm is also idempotent by state.
 */

const base = (e: string) => `/api/v1/engagements/${encodeURIComponent(e)}/discovery`;
const seg = encodeURIComponent;

export const RunStatusSchema = z.enum([
  'created',
  'parsing',
  'reading',
  'ready_for_review',
  'failed',
]);
export type RunStatus = z.infer<typeof RunStatusSchema>;

const RunSchema = z.object({
  id: z.string(),
  status: RunStatusSchema,
  scope: z.string(),
  window_count: z.number().nullable(),
  windows_done: z.number().nullable(),
  proposal_count: z.number().nullable(),
  /** Passages the model proposed that could not be tied to the transcript, so were not kept. */
  dropped_count: z.number().nullable().optional(),
  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
});
export type TranscriptRun = z.infer<typeof RunSchema>;

const TranscriptListSchema = z.object({
  transcripts: z.array(
    z.object({
      transcript_id: z.string(),
      version_no: z.number(),
      kind: z.string(),
      document_title: z.string().nullable(),
      created_at: z.string(),
      evidence_count: z.number(),
      proposed_count: z.number(),
      run: RunSchema.nullable(),
    }),
  ),
});
export type TranscriptVersion = z.infer<typeof TranscriptListSchema>['transcripts'][number];

const SpeakerSchema = z.object({
  id: z.string(),
  label: z.string(),
  person_id: z.string().nullable(),
  person: z
    .object({
      id: z.string(),
      full_name: z.string(),
      title: z.string().nullable(),
      department: z.string().nullable(),
    })
    .nullable(),
  is_interviewer: z.boolean(),
  match_basis: z.string().nullable(),
  possible_duplicate: z.boolean(),
  proposal_count: z.number(),
  /** Matched by the AI reader and not confirmed by a person: a suggestion; its proposals wait. */
  suggested: z.boolean().optional(),
  /** Confirmed evidence cites the speaker: who they are no longer changes (rule 8). */
  frozen: z.boolean().optional(),
  line_count: z.number().optional(),
  sample_line: z.object({ at: z.string().nullable(), text: z.string() }).nullable().optional(),
});
export type Speaker = z.infer<typeof SpeakerSchema>;

const HeaderAttendeeSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  organizer: z.boolean(),
  response: z.enum(['accepted', 'declined', 'tentative', 'needs_action']).nullable(),
  /** On the consulting team: the interviewer's side, never a source. */
  avero: z.boolean(),
  /** The one person in People the attendee is. */
  person: z
    .object({ id: z.string(), full_name: z.string(), title: z.string().nullable() })
    .nullable(),
  /** People the attendee may be: the same-person question (rule 6). */
  candidates: z.array(
    z.object({
      id: z.string(),
      full_name: z.string(),
      title: z.string().nullable(),
      match: z.string(),
    }),
  ),
});
export type HeaderAttendee = z.infer<typeof HeaderAttendeeSchema>;

/** What holds a proposed item back (`evidenceHold` on the server). */
export const HoldSchema = z.enum([
  'person_unresolved',
  'speaker_unconfirmed',
  'interviewer',
  'unmapped',
]);
export type Hold = z.infer<typeof HoldSchema>;

const ProposalSchema = z.object({
  evidence_id: z.string(),
  line_ordinal: z.number().nullable(),
  at: z.string().nullable(),
  speaker_id: z.string().nullable(),
  speaker_label: z.string().nullable(),
  text: z.string(),
  context_line: z
    .object({ at: z.string().nullable(), speaker_label: z.string().nullable(), text: z.string() })
    .nullable(),
  area_ids: z.array(z.string()),
  shown_on_screen: z.boolean(),
  confidence: z.number(),
  signal: z.object({ kind: z.string(), at: z.string().nullable(), text: z.string().nullable() }),
  blocked: HoldSchema.nullable(),
  preselected: z.boolean(),
});
export type Proposal = z.infer<typeof ProposalSchema>;

const TranscriptDetailSchema = z.object({
  transcript: z.object({
    transcript_id: z.string(),
    interview_code: z.string(),
    version_no: z.number(),
    kind: z.string(),
    document_title: z.string().nullable(),
    created_at: z.string(),
    line_count: z.number(),
    changed_line_count: z.number(),
  }),
  run: RunSchema.nullable(),
  speakers: z.array(SpeakerSchema),
  /** Who the header names as invited; null for a version attached before it was kept, or notes. */
  header_attendees: z.array(HeaderAttendeeSchema).nullable().optional(),
  lines: z
    .array(
      z.object({
        id: z.string(),
        ordinal: z.number(),
        at: z.string().nullable(),
        speaker_label: z.string().nullable(),
        speaker_id: z.string().nullable(),
        text: z.string(),
      }),
    )
    .optional(),
  proposals: z.array(ProposalSchema),
  confirmed: z.array(
    z.object({
      evidence_id: z.string(),
      number: z.string(),
      status: z.string(),
      text: z.string(),
      area_ids: z.array(z.string()),
      at: z.string().nullable(),
      version_no: z.number(),
    }),
  ),
});
export type TranscriptDetail = z.infer<typeof TranscriptDetailSchema>;

const SkipSchema = z.object({ id: z.string(), reason: z.string() });
const PreviewSchema = z.object({
  changes: z.array(CoverageChangeSchema),
  confirmable: z.array(z.string()),
  skipped: z.array(SkipSchema),
});
export type ConfirmPreview = z.infer<typeof PreviewSchema>;
const ConfirmSchema = z.object({
  confirmed: z.array(z.object({ id: z.string(), number: z.string() })),
  skipped: z.array(SkipSchema),
  coverage_changes: z.array(CoverageChangeSchema),
});
export type ConfirmResult = z.infer<typeof ConfirmSchema>;

export const DECLINE_REASONS = [
  ['not_about_area', 'Not about this area'],
  ['small_talk', 'Small talk'],
  ['duplicate', 'Duplicate'],
  ['wrong_person', 'Wrong person'],
  ['other', 'Other'],
] as const;
export type DeclineReason = (typeof DECLINE_REASONS)[number][0];

export type NewPerson = {
  full_name: string;
  email?: string;
  match_decision?: { decision: 'different'; candidate_ids: string[] };
};

export type SpeakerMapping =
  | { person_id: string }
  | { is_interviewer: true }
  | { new_person: NewPerson };

/** One speaker of "Who was in the room?", as saved. */
export type RoomEntry = { speaker_id: string } & SpeakerMapping;

const RoomSaveSchema = z.object({
  transcript_id: z.string(),
  saved: z.array(z.string()),
  speakers: z.array(SpeakerSchema),
});

// ---------------------------------------------------------------------------
// The review queue (GET /discovery/review)
// ---------------------------------------------------------------------------

const CitationSchema = z.object({ kind: z.string(), label: z.string(), location: z.string() });

export const ReviewItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  channel: z.string(),
  area_ids: z.array(z.string()),
  shown_on_screen: z.boolean(),
  confidence: z.number().nullable(),
  preselected: z.boolean(),
  source: z.object({ kind: z.string(), id: z.string(), name: z.string() }).passthrough().nullable(),
  citation: CitationSchema.passthrough(),
  interview: z.object({ id: z.string(), code: z.string(), title: z.string() }).nullable(),
  transcript: z.object({ id: z.string(), version_no: z.number(), latest: z.boolean() }).nullable(),
  speaker: z.object({ id: z.string(), label: z.string() }).nullable(),
  document: z.object({ id: z.string(), title: z.string() }).nullable(),
  context_line: z
    .object({ at: z.string().nullable(), speaker_label: z.string().nullable(), text: z.string() })
    .nullable(),
  signal: z
    .object({ kind: z.string(), at: z.string().nullable(), text: z.string().nullable() })
    .nullable(),
  hold: HoldSchema.nullable(),
});
export type ReviewItem = z.infer<typeof ReviewItemSchema>;

const ReviewQueueSchema = z.object({
  items: z.array(ReviewItemSchema),
  held_back: z.array(
    z.object({
      interview_id: z.string(),
      code: z.string(),
      title: z.string(),
      reason: HoldSchema,
      count: z.number(),
    }),
  ),
  counts: z.object({
    ready: z.number(),
    held: z.number(),
    interviews: z.number(),
    documents: z.number(),
  }),
});
export type ReviewQueue = z.infer<typeof ReviewQueueSchema>;

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

const transcripts = (e: string, iid: string) => `${base(e)}/interviews/${seg(iid)}/transcripts`;
const write = <T>(
  path: string,
  schema: z.ZodType<T>,
  body: unknown,
  method: 'POST' | 'PATCH' = 'POST',
) => apiClient(path, schema, { method, body, idempotencyKey: newIdempotencyKey() });

export const listTranscripts = (e: string, iid: string) =>
  apiClient(transcripts(e, iid), TranscriptListSchema);
export const getTranscript = (e: string, iid: string, tid: string, lines: boolean) =>
  apiClient(`${transcripts(e, iid)}/${seg(tid)}`, TranscriptDetailSchema, {
    query: { lines: lines ? 'true' : undefined },
  });
export const attachTranscript = (
  e: string,
  iid: string,
  body: { document_id: string } | { notes_text: string },
) =>
  write(
    transcripts(e, iid),
    z
      .object({
        transcript_id: z.string(),
        version_no: z.number(),
        run_id: z.string(),
        changed_line_count: z.number().optional(),
      })
      .passthrough(),
    body,
  );
export const rereadTranscript = (e: string, iid: string, tid: string) =>
  write(
    `${transcripts(e, iid)}/${seg(tid)}:reread`,
    z.object({ run_id: z.string() }).passthrough(),
    {},
  );
export const mapSpeaker = (
  e: string,
  iid: string,
  tid: string,
  sid: string,
  body: SpeakerMapping,
) => write(`${transcripts(e, iid)}/${seg(tid)}/speakers/${seg(sid)}`, SpeakerSchema, body, 'PATCH');

/** "Who was in the room?": every speaker's mapping in one save. */
export const saveRoom = (e: string, iid: string, tid: string, speakers: RoomEntry[]) =>
  apiClient(`${transcripts(e, iid)}/${seg(tid)}/speakers`, RoomSaveSchema, {
    method: 'PUT',
    body: { speakers },
    idempotencyKey: newIdempotencyKey(),
  });

export const getReviewQueue = (e: string) => apiClient(`${base(e)}/review`, ReviewQueueSchema);

export const editProposal = (
  e: string,
  id: string,
  body: { area_ids?: string[]; shown_on_screen?: boolean },
) => write(`${base(e)}/evidence/${seg(id)}`, z.object({}).passthrough(), body, 'PATCH');
export const declineEvidence = (
  e: string,
  id: string,
  body: { reason_code: DeclineReason; reason_text?: string },
) => write(`${base(e)}/evidence/${seg(id)}:decline`, z.object({}).passthrough(), body);
export const previewConfirm = (e: string, ids: string[]) =>
  apiClient(`${base(e)}/evidence:preview-confirm`, PreviewSchema, {
    method: 'POST',
    body: { ids },
  });
export const confirmEvidence = (e: string, ids: string[]) =>
  write(`${base(e)}/evidence:confirm`, ConfirmSchema, { ids });

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const reviewKeys = {
  transcripts: (e: string, iid: string) => [...discoveryKeys.all(e), 'transcripts', iid] as const,
  transcript: (e: string, iid: string, tid: string, lines: boolean) =>
    [...discoveryKeys.all(e), 'transcript', iid, tid, lines] as const,
  preview: (e: string, ids: readonly string[]) =>
    [...discoveryKeys.all(e), 'confirm-preview', ...ids] as const,
  queue: (e: string) => [...discoveryKeys.all(e), 'review-queue'] as const,
};

const READING: ReadonlySet<RunStatus> = new Set(['created', 'parsing', 'reading']);
export const isReading = (run: { status: RunStatus } | null | undefined) =>
  !!run && READING.has(run.status);

/** While a reading runs, the screen asks again every few seconds for its progress. */
export const READING_POLL_MS = 3000;

export function useTranscriptsQuery(e: string, iid: string) {
  return useQuery({
    queryKey: reviewKeys.transcripts(e, iid),
    queryFn: () => listTranscripts(e, iid),
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.transcripts.some((t) => isReading(t.run)) ? READING_POLL_MS : false,
  });
}

export function useTranscriptQuery(e: string, iid: string, tid: string | null, lines: boolean) {
  return useQuery({
    queryKey: reviewKeys.transcript(e, iid, tid ?? '', lines),
    queryFn: () => getTranscript(e, iid, tid!, lines),
    enabled: !!tid,
    placeholderData: keepPreviousData,
    retry: false,
    refetchInterval: (q) => (isReading(q.state.data?.run) ? READING_POLL_MS : false),
  });
}

/** The coverage effect of confirming `ids`, computed by the API; nothing is written. */
export function useConfirmPreviewQuery(e: string, ids: readonly string[]) {
  const sorted = [...ids].sort();
  return useQuery({
    queryKey: reviewKeys.preview(e, sorted),
    queryFn: () => previewConfirm(e, sorted),
    enabled: sorted.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

/** Every proposed item across interviews and documents, with what holds any back. */
export function useReviewQueueQuery(e: string) {
  return useQuery({
    queryKey: reviewKeys.queue(e),
    queryFn: () => getReviewQueue(e),
    retry: false,
  });
}
